import React from 'react'
import { Button } from 'reactstrap'
import { DataGrid } from '@mui/x-data-grid'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EmailIcon from '@mui/icons-material/Email'
import PhoneIcon from '@mui/icons-material/Phone'
import { withFirebase } from '../../components/Fb'
import { buildStatusUpdate, statusColor, statusHistory, statusLabel, toDisplayDate } from '../../components/utils/inquiries'
import {
  STATUS_FILTERS,
  authorLabel,
  filterInquiries,
  inquiryTitle,
  mailtoHref,
  matchesStatusFilter,
  messagePreview,
  normaliseCustomerInquiry,
  telHref,
  typeLabel,
  typesIn,
} from '../../components/utils/customerInquiries'

/**
 * Tiedustelut - inquiries of every type in customers/{cid}/inquiries (docs/features/inquiries.md).
 * Today they are organisation event inquiries (Yritystapahtumatiedustelu) that partners send
 * through the customer integrations API. manageui never creates them and never edits what the
 * customer wrote: it reads them live, moves the status along and soft-deletes.
 */

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
  } catch (error) {
    // Clipboard needs a secure context and permission; the text is on screen either way.
    console.error('Copying failed', error)
  }
}

function CopyButton({ text }) {
  const [copied, setCopied] = React.useState(false)
  return (
    <Tooltip title={copied ? 'Kopioitu' : 'Kopioi'}>
      <IconButton
        size='small'
        onClick={async () => {
          await copy(text)
          setCopied(true)
        }}
      >
        <ContentCopyIcon fontSize='small' />
      </IconButton>
    </Tooltip>
  )
}

function StatusChip({ status }) {
  return <Chip size='small' label={statusLabel(status)} color={statusColor(status)} />
}

// Email and phone, as links big enough to tap on a phone.
function ContactLinks({ inquiry, onEmail }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {inquiry.email ? (
        <Link href={mailtoHref(inquiry)} onClick={(e) => { e.stopPropagation(); if (onEmail) onEmail() }} sx={{ wordBreak: 'break-all' }}>
          {inquiry.email}
        </Link>
      ) : null}
      {inquiry.phone ? (
        <Link href={telHref(inquiry.phone)} onClick={(e) => e.stopPropagation()}>
          {inquiry.phone}
        </Link>
      ) : null}
    </Box>
  )
}

function InquiryDetails({ inquiry, saving, onStatus, onDelete, onClose, fullScreen }) {
  const [offerResponded, setOfferResponded] = React.useState(false)
  const [showTech, setShowTech] = React.useState(false)
  const [confirmDelete, setConfirmDelete] = React.useState(false)
  const history = statusHistory(inquiry.raw)
  const raw = inquiry.raw

  const setStatus = (status) => {
    setOfferResponded(false)
    onStatus(inquiry, status)
  }

  return (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant='h5' component='span' sx={{ mr: 1 }}>
            {inquiryTitle(inquiry)}
          </Typography>
          <StatusChip status={inquiry.status} />
        </Box>
        <Typography variant='body2' color='text.secondary'>
          {typeLabel(inquiry.type)} · Saapunut {toDisplayDate(inquiry.created) || '—'}
          {inquiry.reference ? ' · Viite ' + inquiry.reference : ''}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant='subtitle1'>{inquiry.name}</Typography>
          {inquiry.organisation && inquiry.name ? (
            <Typography variant='body2' color='text.secondary'>
              {inquiry.organisation}
            </Typography>
          ) : null}
          {inquiry.email ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <EmailIcon fontSize='small' color='action' />
              <Link href={mailtoHref(inquiry)} onClick={() => inquiry.status === 'new' && setOfferResponded(true)} sx={{ wordBreak: 'break-all', py: 0.5 }}>
                {inquiry.email}
              </Link>
              <CopyButton text={inquiry.email} />
            </Box>
          ) : null}
          {inquiry.phone ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon fontSize='small' color='action' />
              <Link href={telHref(inquiry.phone)} sx={{ py: 0.5 }}>
                {inquiry.phone}
              </Link>
              <CopyButton text={inquiry.phone} />
            </Box>
          ) : null}
          {offerResponded ? (
            <Alert
              severity='info'
              sx={{ mt: 1 }}
              action={
                <Button size='sm' color='primary' disabled={saving} onClick={() => setStatus('responded')}>
                  Merkitse vastatuksi
                </Button>
              }
              onClose={() => setOfferResponded(false)}
            >
              Vastasitko tiedusteluun?
            </Alert>
          ) : null}
        </Box>

        {/* The customer's own words, line breaks and all: partners send structured text and a
            draft programme, and its format is theirs, so it is shown as is, never parsed. */}
        <Typography
          component='div'
          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6, maxWidth: '70ch', bgcolor: 'grey.50', border: 1, borderColor: 'grey.200', borderRadius: 1, p: 2 }}
        >
          {inquiry.message || <span style={{ color: '#6c757d' }}>Ei viestiä.</span>}
        </Typography>

        <Divider sx={{ my: 2 }} />
        <Typography variant='subtitle2'>Tilan historia</Typography>
        {history.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            Tilaa ei ole vielä muutettu.
          </Typography>
        ) : (
          history.map((entry, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
              <Typography variant='body2' sx={{ minWidth: 80 }}>
                {statusLabel(entry.status)}
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ minWidth: 130 }}>
                {toDisplayDate(entry.at) || '—'}
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ wordBreak: 'break-word' }}>
                {authorLabel(entry.by) || '—'}
              </Typography>
            </Box>
          ))
        )}

        <Button size='sm' color='link' className='mt-3 px-0' onClick={() => setShowTech(!showTech)}>
          {showTech ? 'Piilota tekniset tiedot' : 'Tekniset tiedot'}
        </Button>
        <Collapse in={showTech}>
          <Box component='table' sx={{ fontSize: 13, '& td': { pr: 2, py: 0.25, verticalAlign: 'top' }, '& td:first-of-type': { color: 'text.secondary' } }}>
            <tbody>
              {[
                ['Tunniste', inquiry.id],
                ['Tyyppi', raw.type],
                ['Viite', raw.reference],
                ['Lähettäjä', raw.creatorId],
                ['Lähde', raw.source],
              ].map(([label, value]) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td style={{ wordBreak: 'break-all' }}>{value || '—'}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Collapse>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button color='danger' outline disabled={saving} onClick={() => setConfirmDelete(true)} style={{ marginRight: 'auto' }}>
          Poista
        </Button>
        {inquiry.status !== 'new' ? (
          <Button color='secondary' outline disabled={saving} onClick={() => setStatus('new')}>
            Palauta uudeksi
          </Button>
        ) : null}
        {inquiry.status === 'new' ? (
          <Button color='primary' disabled={saving} onClick={() => setStatus('responded')}>
            Merkitse vastatuksi
          </Button>
        ) : null}
        {inquiry.status !== 'done' ? (
          <Button color='success' disabled={saving} onClick={() => setStatus('done')}>
            Merkitse valmiiksi
          </Button>
        ) : null}
        <Button color='link' onClick={onClose}>
          Sulje
        </Button>
      </DialogActions>
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Poistetaanko tiedustelu?</DialogTitle>
        <DialogContent>
          <Typography>
            {inquiryTitle(inquiry)}. Tiedustelu poistuu listalta ja myös kumppanin rajapinnasta.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color='link' onClick={() => setConfirmDelete(false)}>
            Peruuta
          </Button>
          <Button
            color='danger'
            onClick={() => {
              setConfirmDelete(false)
              onDelete(inquiry)
            }}
          >
            Poista
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

function InquiriesContainer({ firebase }) {
  const theme = useTheme()
  const narrow = useMediaQuery(theme.breakpoints.down('md'))
  const [inquiries, setInquiries] = React.useState(null)
  const [statusFilter, setStatusFilter] = React.useState('open')
  const [typeFilter, setTypeFilter] = React.useState('all')
  const [search, setSearch] = React.useState('')
  const [selectedId, setSelectedId] = React.useState(null)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState(null)

  React.useEffect(
    () =>
      firebase.listenInquiries(
        (docs) => {
          setInquiries(docs.map(normaliseCustomerInquiry).sort((a, b) => b.createdsort - a.createdsort))
        },
        (err) => {
          console.error('Inquiries failed to load', err)
          setInquiries([])
          setError('Tiedusteluiden lataus epäonnistui.')
        }
      ),
    [firebase]
  )

  const changeStatus = async (inquiry, status) => {
    if (status === inquiry.status) return
    setSaving(true)
    setError(null)
    // Client timestamp for the log entry: Firestore forbids serverTimestamp() inside arrayUnion.
    const result = await firebase.updateInquiryStatus(inquiry.id, buildStatusUpdate(status, firebase.auth.currentUser, firebase.getToday()))
    if (result && result.error) setError('Tilan päivitys epäonnistui: ' + result.error)
    setSaving(false)
  }

  const remove = async (inquiry) => {
    setSaving(true)
    setError(null)
    try {
      await firebase.deleteInquiry(inquiry.id)
      setSelectedId(null)
    } catch (err) {
      console.error('Deleting the inquiry failed', err)
      setError('Poisto epäonnistui.')
    }
    setSaving(false)
  }

  if (inquiries === null) {
    return (
      <div className='content'>
        <CircularProgress size={24} />
      </div>
    )
  }

  const types = typesIn(inquiries)
  const visible = filterInquiries(inquiries, { status: statusFilter, type: typeFilter, search })
  // the live document, so a status the partner changes shows in the open dialog too
  const selected = selectedId ? inquiries.find((i) => i.id === selectedId) : null
  const byType = typeFilter === 'all' ? inquiries : inquiries.filter((i) => i.type === typeFilter)

  const columns = [
    {
      field: 'created',
      headerName: 'Saapunut',
      width: 135,
      valueGetter: (value, row) => row.createdsort,
      renderCell: (params) => toDisplayDate(params.row.created) || '—',
    },
    { field: 'type', headerName: 'Tyyppi', flex: 1, minWidth: 150, valueGetter: (value, row) => typeLabel(row.type) },
    { field: 'organisation', headerName: 'Yritys', flex: 1, minWidth: 120, renderCell: (params) => params.row.organisation || '—' },
    { field: 'name', headerName: 'Yhteyshenkilö', flex: 1, minWidth: 120 },
    {
      field: 'contact',
      headerName: 'Yhteystiedot',
      flex: 1.3,
      minWidth: 170,
      sortable: false,
      renderCell: (params) => <ContactLinks inquiry={params.row} />,
    },
    {
      field: 'message',
      headerName: 'Viesti',
      flex: 2,
      minWidth: 200,
      sortable: false,
      valueGetter: (value, row) => messagePreview(row.message),
    },
    {
      field: 'status',
      headerName: 'Tila',
      width: 100,
      renderCell: (params) => <StatusChip status={params.row.status} />,
    },
  ]

  return (
    <div className='content'>
      {error ? (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
        {STATUS_FILTERS.map((option) => (
          <Chip
            key={option.value}
            label={option.label + ' (' + byType.filter((i) => matchesStatusFilter(i, option.value)).length + ')'}
            clickable
            size='small'
            color='primary'
            variant={statusFilter === option.value ? 'filled' : 'outlined'}
            onClick={() => setStatusFilter(option.value)}
          />
        ))}
        <Box sx={{ flexGrow: 1 }} />
        {types.length > 1 ? (
          <TextField select size='small' label='Tyyppi' value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value='all'>Kaikki tyypit</MenuItem>
            {types.map((t) => (
              <MenuItem key={t} value={t}>
                {typeLabel(t)}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        <TextField size='small' label='Hae' value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: { xs: '100%', sm: 240 } }} />
      </Box>

      {inquiries.length === 0 ? (
        <Alert severity='info'>Ei tiedusteluja. Tiedustelut saapuvat kumppanin lomakkeelta rajapinnan kautta.</Alert>
      ) : visible.length === 0 ? (
        <Typography color='text.secondary'>Ei hakua vastaavia tiedusteluja.</Typography>
      ) : narrow ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {visible.map((i) => (
            <Paper
              key={i.id}
              variant='outlined'
              onClick={() => setSelectedId(i.id)}
              sx={{ p: 1.5, cursor: 'pointer', borderLeft: 4, borderLeftColor: i.status === 'new' ? 'warning.main' : 'grey.300' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant='subtitle1' sx={{ flexGrow: 1, fontWeight: i.status === 'new' ? 700 : 400 }}>
                  {inquiryTitle(i)}
                </Typography>
                <StatusChip status={i.status} />
              </Box>
              <Typography variant='caption' color='text.secondary' display='block'>
                {toDisplayDate(i.created)} · {typeLabel(i.type)}
                {i.organisation && i.name ? ' · ' + i.name : ''}
              </Typography>
              <Typography variant='body2' sx={{ my: 0.5 }}>
                {messagePreview(i.message)}
              </Typography>
              <ContactLinks inquiry={i} />
            </Paper>
          ))}
        </Box>
      ) : (
        <Box sx={{ bgcolor: '#fff' }}>
          <DataGrid
            rows={visible}
            columns={columns}
            density='compact'
            autoHeight
            getRowHeight={() => 'auto'}
            onRowClick={(params) => setSelectedId(params.row.id)}
            getRowClassName={(params) => (params.row.status === 'new' ? 'inquiry-new' : '')}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } }, sorting: { sortModel: [{ field: 'created', sort: 'desc' }] } }}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell': { py: 0.75 },
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              '& .inquiry-new': { bgcolor: '#fff8e1', fontWeight: 600 },
              '& .inquiry-new:hover': { bgcolor: '#ffecb3' },
            }}
          />
        </Box>
      )}

      {selected ? (
        <InquiryDetails
          key={selected.id}
          inquiry={selected}
          saving={saving}
          fullScreen={narrow}
          onStatus={changeStatus}
          onDelete={remove}
          onClose={() => setSelectedId(null)}
        />
      ) : null}
    </div>
  )
}

export default withFirebase(InquiriesContainer)
